import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { Dimmer, Grid, Loader } from "semantic-ui-react";
import { useGetEnergyType } from "../../../api/common";
import { useCreateFacility } from "../../../api/facility";
import {
  useGetCountries,
  useGetHeatSourceType,
  useGetRefrigerantSourceType,
  useGetWaterSourceType
} from "../../../api/static-data";
import { CommonModal } from "../../../components";
import CommonSplitContainer from "../../../components/commonSplitContainer/CommonSplitContainer";
import ConfirmModal from "../../../components/confirmViewModal/ConfirmModal";
import MainBottomMultipleButtonView from "../../../components/mainBottomButtonView/MainBottomMultipleButtonView";
import { FACILITY_OWNERSHIP, FACILITY_TYPES, UNIT_TYPES } from "../../../config/constants";
import {
  DEFAULT_ENARGY_SOURCE,
  DEFAULT_HEAT_SOURCE,
  DEFAULT_REFIGERATION_SOURCE,
  DEFAULT_WATER_SOURCE
} from "../../../config/drawingConstants";
import { errorView } from "../../../helpers/ErrorHandler";
import { hasValidationErrors } from "../../../utils/utils";
import FacilityEnergy from "../facilityForms/FacilityEnergy";
import FacilityHeatSource from "../facilityForms/FacilityHeatSource";
import FacilityInfo from "../facilityForms/FacilityInfo";
import FacilityRefrigerant from "../facilityForms/FacilityRefrigerant";
import FacilityWaterSource from "../facilityForms/FacilityWaterSource";
import "./facilityRegister.scss";

interface FacilityRegisterModalProps {
  visibleModal: boolean;
  setVisibleModal: (open: boolean, data?: any) => void;
  refetchFacilityList?: () => void;
}

const FacilityRegisterModal = ({
  visibleModal,
  setVisibleModal,
  refetchFacilityList
}: FacilityRegisterModalProps) => {
  const [addFacilityEnergy, setFacilityEnergy] = useState<any>([]);
  const [addFacilityHeat, setFacilityHeat] = useState<any>([]);
  const [addFacilityRefrigerant, setFacilityRefrigerant] = useState<any>([]);
  const [addFacilityWater, setFacilityWater] = useState<any>([]);
  const [defaultEnergyType, setDefaultEnergyType] = useState<number>(DEFAULT_ENARGY_SOURCE);
  const [defaultHeatType, setDefaultHeatType] = useState<number>(DEFAULT_HEAT_SOURCE);
  const [defaultWaterType, setDefaultWaterType] = useState<number>(DEFAULT_WATER_SOURCE);
  const [defaultRefrigerant, setDefaultRefrigerantType] = useState<number>(
    DEFAULT_REFIGERATION_SOURCE
  );
  const [defaultFacilityType, setDefaultType] = useState<any>(FACILITY_TYPES[2].value);
  const [defaultFacilityOwnerShip, setDefaultOwnerShip] = useState<any>(
    FACILITY_OWNERSHIP[0].value
  );
  const [getDefaultUnit, setUnit] = useState(UNIT_TYPES[0].value);
  const [getDefaultRefrigerantUnit, setRefrigerantUnit] = useState(UNIT_TYPES[0].value);
  const [country, setCountry] = useState<any>();
  const [deleteRefrigerantData, setDeleteRefrigerantData] = useState<any>({});
  const [visibleHeatDeleteModal, setVisibleHeatDeleteModal] = useState(false);
  const [visibleWaterDeleteModal, setVisibleWaterDeleteModal] = useState(false);
  const [visibleRefrigerantDeleteModal, setVisibleRefrigerantDeleteModal] = useState(false);
  const [visibleDeleteModal, setVisibleDeleteModal] = useState(false);
  const [deleteData, setDeleteData] = useState<any>({});
  const [deleteHeatData, setDeleteHeatData] = useState<any>({});
  const [deleteWaterData, setDeleteWaterData] = useState<any>({});

  //get energy type data
  const { data: energyType, status, fetchStatus, refetch } = useGetEnergyType();
  const {
    data: refrigerantSource,
    status: refrigerantStatus,
    fetchStatus: refrigerantFetchStatus,
    refetch: refrigerantFetch
  } = useGetRefrigerantSourceType();

  const {
    data: heatSource,
    status: heatSourceStatus,
    fetchStatus: fetchHeatSource,
    refetch: refetchHeatSource
  } = useGetHeatSourceType();
  const {
    data: waterSource,
    status: waterSourceStatus,
    fetchStatus: fetchWaterSource,
    refetch: refetchWaterSource
  } = useGetWaterSourceType();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
    getValues,
    setValue
  } = useForm();

  const {
    formState: { errors: errorsFacilityEnergy },
    handleSubmit: handleSubmitFacilityEnergy,
    setValue: setEnergyValue,
    reset: resetEnergy,
    control,
    setError
  } = useForm();

  const {
    formState: { errors: errorsFacilityHeat },
    handleSubmit: handleSubmitFacilityHeat,
    setValue: setHeatValue,
    reset: resetHeat,
    control: controlHeat
  } = useForm();

  const {
    formState: { errors: errorsFacilityWater },
    handleSubmit: handleSubmitFacilityWater,
    setValue: setWaterValue,
    reset: resetWater,
    control: controlWater
  } = useForm();

  const {
    formState: { errors: errorRefrigerant },
    handleSubmit: handleSubmitFacilityRefrigerant,
    setValue: setRefrigerantValue,
    reset: resetRefrigerant,
    control: controlRefrigerant
  } = useForm();

  const {
    data: countriesData,
    isSuccess: isCountriesDataSuccess,
    isLoading: isCountriesDataLoading
  } = useGetCountries();

  //custom hook create facility
  const { isLoading, mutate, data, reset: resetData } = useCreateFacility();

  useEffect(() => {
    return () => {
      setFacilityEnergy([]);
      setFacilityHeat([]);
      reset();
      resetFacilityEnergys();
      resetFacilityHeat();
      resetFacilityWater();
      resetEnergy();
      resetHeat();
      resetWater();
      setDefaultOwnerShip(FACILITY_OWNERSHIP[0].value);
    };
  }, [visibleModal]);

  const countries = useMemo(() => {
    if (isCountriesDataSuccess) {
      return countriesData?.map((c: any) => ({
        key: c.id,
        text: c.country,
        value: c.id
      }));
    }
  }, [countriesData, isCountriesDataSuccess]);

  const closeModalStatus = () => {
    const value = getValues();
    setVisibleModal(false, value);
  };

  //check product error view
  const checkFacilitySaveCondition = (data: any) => {
    if (!country) {
      errorView("Facility country is required", { force: true });
      return true;
    } else if (addFacilityEnergy.length <= 0) {
      errorView("Please add facility energy", { force: true });
      return true;
    } else {
      return false;
    }
  };

  //Reset facility energys
  const resetFacilityEnergys = () => {
    setDefaultEnergyType(DEFAULT_ENARGY_SOURCE);
    setEnergyValue("Percentage", null);
    setEnergyValue("Price", 0.0);
    setEnergyValue("Year", null);
    setEnergyValue("kWhConsumed", null);
  };

  //Reset facility heat
  const resetFacilityHeat = () => {
    setDefaultHeatType(DEFAULT_HEAT_SOURCE);
    setHeatValue("Percentage", null);
    setHeatValue("Price", 0.0);
    setHeatValue("Volumes", null);
    setHeatValue("Year", null);
    setUnit(UNIT_TYPES[0].value);
  };

  //Reset facility water
  const resetFacilityWater = () => {
    setDefaultWaterType(DEFAULT_WATER_SOURCE);
    setWaterValue("Price", 0.0);
    setWaterValue("Volumes", null);
    setWaterValue("Year", null);
  };

  //save product data
  const onSubmit = async (data: any) => {
    if (!checkFacilitySaveCondition(data)) {
      let {
        facilityName,
        facilityAddress,
        facilityCity,
        facilityType = defaultFacilityType,
        facilityOwnership = defaultFacilityOwnerShip
      } = data;

      let facilityData = {
        facilityName,
        facilityAddress,
        facilityCity,
        facilityCountry: country,
        facilityType,
        facilityOwnership,
        energy: addFacilityEnergy,
        heatSource: addFacilityHeat,
        waterSource: addFacilityWater,
        refrigerantSource: addFacilityRefrigerant,
        originalId: null
      };
      mutate(
        { data: facilityData, simulatedFacility: false },
        {
          onSuccess: response => {
            refetchFacilityList?.();
            setVisibleModal(false, { newFacilityId: response?._id });
          }
        }
      );
    }
  };

  const saveFacilityData = (values: any) => {
    setValue("enableNavigate", false);
    onSubmit(values);
  };

  const checkFacilityEnergyError = hasValidationErrors(
    errors,
    errorsFacilityEnergy,
    errorsFacilityHeat,
    errorsFacilityWater,
    errorRefrigerant
  );

  if (
    isLoading ||
    (status === "loading" && fetchStatus === "fetching") ||
    (heatSourceStatus === "loading" && fetchHeatSource === "fetching") ||
    (waterSourceStatus === "loading" && fetchWaterSource === "fetching") ||
    (refrigerantStatus === "loading" && refrigerantFetchStatus === "fetching")
  ) {
    return (
      <Dimmer active>
        <Loader content="Loading" />
      </Dimmer>
    );
  }

  const deleteFacilityEnergy = () => {
    if (visibleDeleteModal) {
      const newData = addFacilityEnergy;
      newData.splice(deleteData.index, 1);
      setFacilityEnergy([...newData]);
    } else if (visibleHeatDeleteModal) {
      const newData = addFacilityHeat;
      newData.splice(deleteHeatData.index, 1);
      setFacilityHeat([...newData]);
    } else if (visibleRefrigerantDeleteModal) {
      const newData = addFacilityRefrigerant;
      newData.splice(deleteRefrigerantData.index, 1);
      setFacilityRefrigerant([...newData]);
    } else {
      const newData = addFacilityWater;
      newData.splice(deleteWaterData.index, 1);
      setFacilityWater([...newData]);
    }
  };

  return (
    <>
      <CommonModal
        modalOpenState={visibleModal}
        modalCloseState={() => {
          closeModalStatus();
          reset();
        }}
        centered={true}
        title="Add New Facility"
      >
        <Grid>
          <Grid.Column computer={16} tablet={16} mobile={16}>
            <CommonSplitContainer
              currentHeight={0.91}
              topChild={
                <>
                  <FacilityInfo
                    register={register}
                    errors={errors}
                    country={country}
                    countries={countries}
                    setFacilityValue={setValue}
                    setCountry={setCountry}
                    isCountriesDataLoading={isCountriesDataLoading}
                    FACILITY_TYPES={FACILITY_TYPES}
                    defaultFacilityType={defaultFacilityType}
                    defaultFacilityOwnerShip={defaultFacilityOwnerShip}
                    setDefaultOwnerShip={setDefaultOwnerShip}
                    setDefaultType={setDefaultType}
                  />
                  <FacilityEnergy
                    energyType={energyType}
                    defaultEnergyType={defaultEnergyType}
                    setDefaultEnergyType={setDefaultEnergyType}
                    errorsFacilityEnergy={errorsFacilityEnergy}
                    handleSubmitFacilityEnergy={handleSubmitFacilityEnergy}
                    addFacilityEnergy={addFacilityEnergy}
                    currentFacilityData={addFacilityEnergy || []}
                    setDeleteData={setDeleteData}
                    setVisibleDeleteModal={setVisibleDeleteModal}
                    control={control}
                    setError={setError}
                    setValue={setEnergyValue}
                    reset={resetEnergy}
                  />
                  <FacilityHeatSource
                    heatType={heatSource}
                    defaultHeatType={defaultHeatType}
                    setDefaultHeatType={setDefaultHeatType}
                    errorsFacilityHeat={errorsFacilityHeat}
                    handleSubmitFacilityHeat={handleSubmitFacilityHeat}
                    addFacilityHeat={addFacilityHeat}
                    currentFacilityHeatData={addFacilityHeat || []}
                    setDeleteHeatData={setDeleteHeatData}
                    setVisibleDeleteHeatModal={setVisibleHeatDeleteModal}
                    control={controlHeat}
                    setValue={setHeatValue}
                    setUnit={setUnit}
                    getDefaultUnit={getDefaultUnit}
                    reset={resetHeat}
                  />
                  <FacilityWaterSource
                    waterType={waterSource}
                    defaultWaterType={defaultWaterType}
                    setDefaultWaterType={setDefaultWaterType}
                    errorsFacilityWater={errorsFacilityWater}
                    handleSubmitFacilityWater={handleSubmitFacilityWater}
                    addFacilityWater={addFacilityWater}
                    currentFacilityWaterData={addFacilityWater || []}
                    setDeleteWaterData={setDeleteWaterData}
                    setVisibleDeleteWaterModal={setVisibleWaterDeleteModal}
                    control={controlWater}
                    setValue={setWaterValue}
                    reset={resetFacilityWater}
                  />
                  <FacilityRefrigerant
                    refrigerantType={refrigerantSource}
                    defaultRefrigerantType={defaultRefrigerant}
                    setDefaultRefrigerantType={setDefaultRefrigerantType}
                    errorsFacilityRefrigerant={errorRefrigerant}
                    handleSubmitFacilityRefrigerant={handleSubmitFacilityRefrigerant}
                    addFacilityRefrigerant={addFacilityRefrigerant}
                    currentFacilityRefrigerantData={addFacilityRefrigerant || []}
                    setDeleteRefrigerantData={setDeleteRefrigerantData}
                    setVisibleDeleteRefrigerantModal={setVisibleRefrigerantDeleteModal}
                    control={controlRefrigerant}
                    setValue={setRefrigerantValue}
                    setUnit={setRefrigerantUnit}
                    getDefaultUnit={getDefaultRefrigerantUnit}
                    reset={resetRefrigerant}
                  />
                </>
              }
              bottomChild={
                <MainBottomMultipleButtonView
                  leftButtons={[
                    {
                      title: "Cancel",
                      onClick: () => closeModalStatus(),
                      buttonOutLine: true
                    }
                  ]}
                  rightButtons={[
                    {
                      contentView: 16,
                      title: "Save Facility",
                      onClick: () => handleSubmit(saveFacilityData)(),
                      type: "submit",
                      buttonView: true,
                      disabled: checkFacilityEnergyError
                    }
                  ]}
                />
              }
            />
          </Grid.Column>
        </Grid>
      </CommonModal>
      <ConfirmModal
        viewModal={
          visibleDeleteModal ||
          visibleHeatDeleteModal ||
          visibleWaterDeleteModal ||
          visibleRefrigerantDeleteModal
        }
        closeModal={() => setVisibleDeleteModal(false)}
        cancel={() => {
          setVisibleDeleteModal(false);
          setDeleteData({});
          setVisibleHeatDeleteModal(false);
          setDeleteHeatData({});
          setVisibleWaterDeleteModal(false);
          setDeleteWaterData({});
          setVisibleRefrigerantDeleteModal(false);
          setDeleteRefrigerantData({});
        }}
        approve={() => {
          setVisibleDeleteModal(false);
          setVisibleHeatDeleteModal(false);
          setVisibleWaterDeleteModal(false);
          setVisibleRefrigerantDeleteModal(false);
          deleteFacilityEnergy();
        }}
        title="Delete Facility Energy"
        subTitle="Are you sure you want to delete facility energy?"
      />
    </>
  );
};

export default FacilityRegisterModal;
